import { api } from './client.js';
import { API_BASE_URL } from './config.js';
import { getAuthorizationHeader } from './auth.js';
import {
  invalidateCompanyProfileCache,
  loadCompanyProfile,
} from '../companyProfileCache.js';

export const getMyCompanyProfile = async (options) =>
  loadCompanyProfile(Boolean(options?.force));

export const getCompanyDashboard = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.chartsOnly) params.set('chartsOnly', '1');
  else if (options.includeCharts === false) params.set('includeCharts', '0');
  const qs = params.toString();
  return api.get(`/company/profile/dashboard${qs ? `?${qs}` : ''}`);
};

export const updateMyCompanyProfile = async (payload) => {
  const result = await api.put('/company/profile', payload);
  invalidateCompanyProfileCache();
  return result;
};

export { invalidateCompanyProfileCache };

export const uploadCompanyLogo = async (file) => {
  const formData = new FormData();
  formData.append('logo', file);
  const headers = {};
  const auth = getAuthorizationHeader();
  if (auth) headers.Authorization = auth;

  const response = await fetch(`${API_BASE_URL}/company/profile/logo`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to upload company logo');
  }
  invalidateCompanyProfileCache();
  return data.data;
};

export const removeCompanyLogo = async () => {
  const headers = {};
  const auth = getAuthorizationHeader();
  if (auth) headers.Authorization = auth;

  const response = await fetch(`${API_BASE_URL}/company/profile/logo`, {
    method: 'DELETE',
    headers,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to remove company logo');
  }
  invalidateCompanyProfileCache();
  return data.data;
};

/** Fetch company logo via API proxy (avoids S3 CORS for canvas/export). */
export const fetchCompanyLogoDataUrl = async () => {
  const headers = {};
  const auth = getAuthorizationHeader();
  if (auth) headers.Authorization = auth;

  const response = await fetch(`${API_BASE_URL}/company/profile/logo`, { headers });
  if (response.status === 404) return '';
  if (!response.ok) {
    throw new Error('Failed to load company logo');
  }

  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read company logo'));
    reader.readAsDataURL(blob);
  });
};

/** Authenticated logo URL for img src (same-origin API, no S3 CORS). */
export const getCompanyLogoProxyUrl = () => `${API_BASE_URL}/company/profile/logo`;

export const getMyProfile = async () => api.get('/company/profile/me');

export const updateMyProfile = async (payload) => api.put('/company/profile/me', payload);

export const changeMyPassword = async (payload) => api.put('/company/profile/me/password', payload);

export const getBillingOverview = async () => api.get('/company/profile/billing-overview');

export const getBillingHistory = async () => api.get('/company/profile/billing-history');

export const getUsageSummary = async () => api.get('/company/profile/usage-summary');

export const getUsageHistory = async (params = {}) => {
  const clean = Object.entries(params).reduce((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = v;
    return acc;
  }, {});
  const qs = new URLSearchParams(clean).toString();
  return api.get(`/company/profile/usage-history${qs ? `?${qs}` : ''}`);
};

export const getPaymentMethods = async () => api.get('/company/profile/payment-methods');

export const setDefaultPaymentMethod = async (id) => api.patch(`/company/profile/payment-methods/${id}/default`);

export const deletePaymentMethod = async (id) => api.delete(`/company/profile/payment-methods/${id}`);

export const previewCoupon = async (payload) => api.post('/company/profile/preview-coupon', payload);

export const previewAnnualBilling = async (payload) =>
  api.post('/company/profile/preview-annual-billing', payload);

/** @deprecated use previewAnnualBilling */
export const previewAnnualCoupon = previewAnnualBilling;

export const getPlanChangePreview = async (planId, billingCycle = null) => {
  const params = new URLSearchParams({ planId: String(planId) });
  if (billingCycle) params.set('billingCycle', billingCycle);
  return api.get(`/company/profile/plan-change-preview?${params.toString()}`);
};

export const schedulePlanChange = async (payload) =>
  api.post('/company/profile/schedule-plan-change', payload);

export const createCheckoutOrder = async (payload) => api.post('/company/profile/create-checkout-order', payload);

export const verifyCheckoutPayment = async (payload) => api.post('/company/profile/verify-checkout-payment', payload);

export const reportCheckoutFailure = async (payload) =>
  api.post('/company/profile/report-checkout-failure', payload);
